/**
 * HTML de los tooltips de hover sobre parcelas del mapa.
 *
 * Lo consume `useParcelHover`, que crea el `maplibregl.Popup` con className
 * `terrio-parcel-tooltip` (estilos en `src/Map/Map.css`).
 *
 * Variantes:
 * - Catastral / “Agregar lotes → Desde el mapa”: dos líneas, alto fijo 54px
 *   (`renderParcelHoverTooltipHtml` / `buildParcelHoverTooltipHtml`).
 * - Lista compartida `/shared/:token`: misma card apilada que CAD `/#/lista/:id`
 *   (`buildSharedListLotTooltipHtml` + clases `terrio-cad-share-tooltip__*`).
 */

import { getDisplayUnits } from './userLotUnits';
import {
  formatListingPrice,
  getStockStatusLabel,
} from './listingCoreConstants';
import { resolveUnitStockStatus } from './userLotFormUtils';
import { formatSurfaceDisplay, parseSurfaceM2 } from './surfaceDisplay';
import {
  formatLotUserContentPrice,
  hasLotUserContentCommercialData,
} from './lotUserContentFormUtils';
import { getCadStockColor } from '../../Showroom/utils/cadStockStatus';

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const toText = (value) => {
  if (value == null) return null;
  const text = String(value).trim();
  return text.length > 0 ? text : null;
};

/**
 * Arma el HTML del tooltip a partir de sus dos líneas.
 * @param {{ title?: string|null, meta?: string|null }} lines
 * @returns {string|null} HTML, o null si no hay nada que mostrar.
 */
export const renderParcelHoverTooltipHtml = ({ title = null, meta = null } = {}) => {
  const titleText = toText(title);
  const metaText = toText(meta);
  if (!titleText && !metaText) return null;

  const html = [];
  if (titleText) {
    html.push(`<div class="terrio-parcel-tooltip__title">${escapeHtml(titleText)}</div>`);
  }
  if (metaText) {
    html.push(`<div class="terrio-parcel-tooltip__meta">${escapeHtml(metaText)}</div>`);
  }
  return html.join('');
};

const formatUrbanSurface = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${n.toLocaleString('es-AR', { maximumFractionDigits: 2 })} m²`;
};

/**
 * Tooltip con los datos del tile PMTiles: catastral y superficie urbana.
 * @param {Object} [properties] Props del feature (`name`, `urbansurface`).
 * @returns {string|null}
 */
export const buildParcelHoverTooltipHtml = (properties = {}) => {
  const urbanLabel = formatUrbanSurface(properties?.urbansurface);
  const name = toText(properties?.name);
  return renderParcelHoverTooltipHtml({
    title: name ? `Número Catastral: ${name}` : null,
    meta: urbanLabel ? `Superficie urbana: ${urbanLabel}` : null,
  });
};

const buildUnitTitle = (unit) => {
  const label = toText(unit?.unit_label);
  const title = toText(unit?.title);
  if (label && title) return `${label} · ${title}`;
  return label || title || null;
};

const buildCadastralLabel = (lot) => {
  const name = toText(lot?.lot_name) || toText(lot?.lot_id);
  return name ? `Número Catastral: ${name}` : null;
};

const buildCadastralShort = (lot) => (
  toText(lot?.lot_name) || toText(lot?.lot_id) || null
);

const formatSurfaceLabel = (value) => {
  const display = formatSurfaceDisplay(parseSurfaceM2(value));
  return display ? `${display.value} ${display.unit}` : null;
};

/** UF: cubierta + semicubierta. Lote: urbansurface / surface del payload. */
const resolveSharedSurfaceLabel = (unit, lot) => {
  if (unit) {
    const covered = parseSurfaceM2(unit.covered_surface);
    const open = parseSurfaceM2(unit.open_surface);
    const hasCovered = !Number.isNaN(covered);
    const hasOpen = !Number.isNaN(open);
    if (hasCovered || hasOpen) {
      return formatSurfaceLabel((hasCovered ? covered : 0) + (hasOpen ? open : 0));
    }
    return formatSurfaceLabel(unit.total_surface);
  }

  return formatSurfaceLabel(
    lot?.urbansurface
    ?? lot?.lot_urbansurface
    ?? lot?.surface
    ?? lot?.lot_surface
    ?? lot?.filter_attributes?.urbansurface
    ?? lot?.filter_attributes?.surface,
  );
};

/**
 * Card apilada del share CAD (`terrio-cad-share-tooltip__*`). Misma tipografía,
 * padding y footer que `/#/lista/:id`.
 *
 * @param {{
 *   title?: string|null,
 *   mutedLines?: Array<string|null|undefined>,
 *   statusLabel?: string|null,
 *   statusColor?: string|null,
 *   price?: string|null,
 *   lotId?: string|number|null,
 * }} opts
 * @returns {string|null}
 */
export const renderCadShareTooltipHtml = ({
  title = null,
  mutedLines = [],
  statusLabel = null,
  statusColor = null,
  price = null,
  lotId = null,
} = {}) => {
  const titleText = toText(title);
  const muted = (Array.isArray(mutedLines) ? mutedLines : [])
    .map(toText)
    .filter(Boolean);
  const statusText = toText(statusLabel);
  const priceText = toText(price);
  if (!titleText && !muted.length && !statusText && !priceText) return null;

  const rows = [];
  if (titleText) {
    rows.push(`<div class="terrio-cad-share-tooltip__title">${escapeHtml(titleText)}</div>`);
  }
  muted.forEach((line) => {
    rows.push(`<div class="terrio-cad-share-tooltip__muted">${escapeHtml(line)}</div>`);
  });
  if (statusText) {
    const color = statusColor || getCadStockColor('available');
    rows.push(
      `<div class="terrio-cad-share-tooltip__status">`
      + `<span class="terrio-cad-share-tooltip__dot" style="background:${escapeHtml(color)}" aria-hidden="true"></span>`
      + `<span>${escapeHtml(statusText)}</span>`
      + `</div>`,
    );
  }
  if (priceText) {
    rows.push(`<div class="terrio-cad-share-tooltip__price">${escapeHtml(priceText)}</div>`);
  }
  rows.push(
    `<div class="terrio-cad-share-tooltip__footer">Click para ver detalle <span aria-hidden="true">→</span></div>`,
  );
  const idAttr = lotId != null && lotId !== ''
    ? ` data-shared-lot-id="${escapeHtml(String(lotId))}"`
    : '';
  return `<div class="terrio-cad-share-tooltip__card" role="button" tabindex="0"${idAttr}>${rows.join('')}</div>`;
};

/**
 * Tooltip de un lote de lista compartida: misma card que CAD `/#/lista/:id`
 * (nombre, m², estado con puntito, precio, “Ver detalle”). Prioriza la
 * publicación sobre los datos del tile.
 * @param {Object} lot Lote agrupado (con `units`) de la lista compartida.
 * @returns {string|null}
 */
export const buildSharedListLotTooltipHtml = (lot) => {
  if (!lot) return null;

  const units = getDisplayUnits(lot);
  const cadastralShort = buildCadastralShort(lot);
  const lotId = lot?.lot_id ?? null;

  if (units.length === 1) {
    const unit = units[0];
    const status = resolveUnitStockStatus(unit);
    const unitTitle = buildUnitTitle(unit);
    return renderCadShareTooltipHtml({
      title: unitTitle || cadastralShort,
      mutedLines: [resolveSharedSurfaceLabel(unit, lot)],
      statusLabel: getStockStatusLabel(status),
      statusColor: getCadStockColor(status),
      price: formatListingPrice(unit?.price, unit?.currency),
      lotId,
    });
  }

  if (units.length > 1) {
    return renderCadShareTooltipHtml({
      title: `${units.length} unidades publicadas`,
      mutedLines: [cadastralShort, resolveSharedSurfaceLabel(null, lot)],
      lotId,
    });
  }

  const content = lot?.lot_user_content ?? null;
  const contentTitle = toText(content?.title);
  const location = [lot?.province_name, lot?.department_name].filter(Boolean).join(' · ');
  const status = content?.stock_status
    ? resolveUnitStockStatus(content)
    : null;
  const price = hasLotUserContentCommercialData(content)
    ? formatLotUserContentPrice(content)
    : null;

  return renderCadShareTooltipHtml({
    title: contentTitle || cadastralShort || buildCadastralLabel(lot),
    mutedLines: [
      resolveSharedSurfaceLabel(null, lot),
      contentTitle && cadastralShort ? cadastralShort : null,
      toText(location),
    ],
    statusLabel: status ? getStockStatusLabel(status) : null,
    statusColor: status ? getCadStockColor(status) : null,
    price,
    lotId,
  });
};
